import { useCallback, useEffect, useRef, useState } from 'react';

import { CollectionStats, Listing, describeApiError, getListings, getStatistics } from '../api/backend';

const CACHE_TTL_MS = 2 * 60 * 1000;

interface CacheEntry {
    at: number;
    listings: Listing[];
    stats: CollectionStats | null;
}

// Module-level cache so switching between collections and back feels instant.
const cache = new Map<string, CacheEntry>();

export interface CollectionData {
    listings: Listing[] | null;
    stats: CollectionStats | null;
    loading: boolean;
    error: string | null;
    refetch: () => void;
}

export const useCollectionData = (collectionPath: string): CollectionData => {
    const [listings, setListings] = useState<Listing[] | null>(null);
    const [stats, setStats] = useState<CollectionStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const generation = useRef(0);

    const load = useCallback(
        async (force: boolean) => {
            const requestId = ++generation.current;
            const cached = cache.get(collectionPath);
            if (!force && cached && Date.now() - cached.at < CACHE_TTL_MS) {
                setListings(cached.listings);
                setStats(cached.stats);
                setLoading(false);
                setError(null);
                return;
            }

            setLoading(true);
            setError(null);
            setListings(null);
            setStats(null);

            // Stats are decorative: their failure must not take the listings down with them.
            const [listingsResult, statsResult] = await Promise.allSettled([
                getListings(collectionPath),
                getStatistics(collectionPath),
            ]);
            if (requestId !== generation.current) return; // a newer request took over

            if (listingsResult.status === 'rejected') {
                setLoading(false);
                setError(describeApiError(listingsResult.reason));
                return;
            }

            const freshListings = listingsResult.value;
            const freshStats = statsResult.status === 'fulfilled' ? statsResult.value : null;
            cache.set(collectionPath, { at: Date.now(), listings: freshListings, stats: freshStats });
            setListings(freshListings);
            setStats(freshStats);
            setLoading(false);
        },
        [collectionPath]
    );

    useEffect(() => {
        load(false);
    }, [load]);

    const refetch = useCallback(() => {
        load(true);
    }, [load]);

    return { listings, stats, loading, error, refetch };
};
